import { UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  MigrationGroup,
  MigrationGroupDetailData,
  UpdateMigrationGroupRequest,
  deleteMigrationGroup,
  getMigrationGroup,
  updateMigrationGroup,
} from '../api/migrationGroups';

// Shared group-detail query (FD-ADR-030). GroupLayout owns the canonical
// observer; the three tab children call this same hook, so react-query dedupes
// to a SINGLE fetch and every tab reads one cache entry — the
// useTenantDetail.ts pattern, and the reason the detail page's device fan-out
// is paid once per visit rather than once per tab.
export function useGroupDetailQuery(
  groupId: string | undefined,
): UseQueryResult<MigrationGroupDetailData> {
  return useQuery<MigrationGroupDetailData>({
    queryKey: ['migration-group', groupId],
    queryFn: () => getMigrationGroup(groupId!),
    enabled: Boolean(groupId),
    // WITHOUT THIS THE PAGE FETCHES TWICE PER VISIT, and for this endpoint that
    // means paying the device fan-out twice.
    //
    // GroupLayout returns a skeleton while loading, so <Outlet/> — and therefore
    // the tab child — does not mount until the first fetch RESOLVES. The child's
    // observer then subscribes to data that is already stale (react-query's
    // default staleTime is 0) and `refetchOnMount` fires a second request. The
    // two observers never overlap, so nothing dedupes them.
    //
    // A short staleTime closes the window. It is not a cache-tuning preference:
    // it is what makes the layout-owns-the-query pattern actually deliver the
    // single fetch it exists for. Tab switches within a visit reuse it too.
    staleTime: 30_000,
    retry: (failureCount, error) => {
      // Never retry a 404 — the group genuinely does not exist, and retrying
      // makes a deleted-group link feel like a slow page rather than a dead one.
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 1;
    },
  });
}

export function useUpdateGroup(groupId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<MigrationGroup, unknown, UpdateMigrationGroupRequest>({
    mutationFn: (req) => updateMigrationGroup(groupId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['migration-group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['migration-groups'] });
      // A rename changes the label projected onto migration rows, so the
      // migrations list is stale too — even though no migration row was written.
      queryClient.invalidateQueries({ queryKey: ['migrations'] });
    },
  });
}

export function useDeleteGroup(groupId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, void>({
    mutationFn: () => deleteMigrationGroup(groupId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['migration-groups'] });
    },
  });
}
